import { MenuItem, Stack } from '@mui/material'
import { CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { vesselVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ThreeDotMenu } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { FC } from 'react'
import { useNavigate } from 'react-router'
import { VesselVisitTableDto } from '../../Stores/VesselVisitViewStore'
import { CancelVisitDialog } from './CancelVisitDialog'

type VesselVisitMenuProps = {
  visit: VesselVisitTableDto
}

export const VesselVisitMenu: FC<VesselVisitMenuProps> = ({ visit }) => {
  const { id, status, orderIds } = visit
  const { dialogStore, appViewStore } = usePlanningStore()
  const { t } = useTranslate()
  const navigate = useNavigate()

  const isCancelled = status === CarrierVisitStatus.Cancelled
  const isExpected = status === CarrierVisitStatus.Expected
  const hasOrders = (orderIds?.length ?? 0) > 0
  const isClosed = status === CarrierVisitStatus.Closed

  const canEdit = !isClosed && !isCancelled
  const canCancel = isExpected && !hasOrders

  const handleCancelVisitDialog = (visit: VesselVisitTableDto) => {
    dialogStore.openDialog(
      <CancelVisitDialog
        identifier={visit.number ?? visit.id.toString()}
        onSubmit={async () => {
          await vesselVisitService.cancelVisit({ id: visit.id })
          appViewStore.setShowAlert('success', t('visitCancelled', 'Visit cancelled successfully'))
          dialogStore.close()
        }}
      />,
      false,
      {
        open: true,
        maxWidth: 'xs',
        slotProps: {
          paper: { sx: { minHeight: '20%' } },
        },
      },
    )
  }

  const onEdit = (visitId: number) => {
    navigate(`${PATHS.visits.vesselPlanning.url}/${visitId}`, {
      state: { from: 'vessel-visits' },
    })
  }

  const onCancel = (visit: VesselVisitTableDto) => {
    handleCancelVisitDialog(visit)
  }

  const onViewDashboard = (visitId: number) => {
    navigate(`${PATHS.visits.vesselVisits.url}/${visitId}/dashboard`, {
      state: { from: 'vessel-visits' },
    })
  }

  const onActivate = async (visitId: number) => {
    await vesselVisitService.reactivateVisit({ id: visitId })
    appViewStore.setShowAlert('success', t('visitReactivated', 'Visit reactivated successfully'))
  }

  const items = [
    {
      key: 'view',
      label: t('view', 'View'),
      onClick: () => onViewDashboard(id),
    },
    {
      key: 'edit',
      label: t('edit', 'Edit'),
      onClick: () => onEdit(id),
      disabled: !canEdit,
    },
    isCancelled
      ? {
          key: 'reactivate',
          label: t('reactivateVisit', 'Reactivate visit'),
          onClick: () => onActivate(id),
        }
      : {
          key: 'cancel',
          label: t('cancelVisit', 'Cancel visit'),
          onClick: () => onCancel(visit),
          disabled: !canCancel,
        },
  ]

  return (
    <Stack data-cy='visits-table-dots-menu'>
      <ThreeDotMenu>
        {items.map(item => (
          <MenuItem
            key={item.key}
            data-cy={`visits-table-dots-menu-${item.key}`}
            onClick={item.onClick}
            disabled={item.disabled}
          >
            {item.label}
          </MenuItem>
        ))}
      </ThreeDotMenu>
    </Stack>
  )
}
